/**
 * Purpose: Module-scoped {@link UserManager} singleton plus a non-React
 *          {@link getAccessToken} helper. Exists so JSON-RPC clients
 *          instantiated at module load (outside the React tree) can
 *          fetch the current bearer token without prop-drilling auth
 *          state.
 *
 * Implementation notes:
 *   - The singleton is created lazily by {@link createUserManager}; calling
 *     it more than once returns the existing instance. Re-mounting
 *     <AuthProvider> with a different config is therefore unsupported by
 *     design — auth config is process-global.
 *   - {@link getAccessToken} returns an empty string when the user is
 *     absent or the access token has expired. Callers that want stricter
 *     behaviour should check `useAuth().isAuthenticated` first.
 *   - Tokens are persisted in `sessionStorage` (oidc-client-ts default
 *     when a userStore isn't supplied — matched explicitly here so the
 *     choice is visible in code review). Per-tab scope avoids the
 *     localStorage cross-tab leakage class without breaking page reload.
 *
 * Created: 2026-04-30 11:00
 *
 * Changelog:
 *   - 2026-04-30 11:00 — Initial version.
 *   - 2026-04-30 12:35 — When `orgId` is set, append the Zitadel
 *                        reserved scope `urn:zitadel:iam:org:id:<orgId>`
 *                        so the authorize call targets that org's login
 *                        policy. Without this Zitadel uses the instance
 *                        default policy and ignores the org-scoped IdP.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
const DEFAULT_SCOPE = 'openid profile email offline_access urn:zitadel:iam:user:resourceowner';
let _um = null;
export function createUserManager(config) {
    if (_um)
        return _um;
    const redirectUri = config.redirectUri ?? window.location.origin + window.location.pathname;
    const postLogoutRedirectUri = config.postLogoutRedirectUri ?? redirectUri;
    const baseScope = config.scope ?? DEFAULT_SCOPE;
    const scope = config.orgId
        ? `${baseScope} urn:zitadel:iam:org:id:${config.orgId}`
        : baseScope;
    _um = new UserManager({
        authority: config.issuer.replace(/\/$/, ''),
        client_id: config.clientId,
        redirect_uri: redirectUri,
        post_logout_redirect_uri: postLogoutRedirectUri,
        response_type: 'code',
        scope,
        automaticSilentRenew: true,
        loadUserInfo: false,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        extraQueryParams: config.idpHint ? { idp_id_hint: config.idpHint } : undefined,
    });
    return _um;
}
export function getUserManager() {
    if (!_um) {
        throw new Error('@ada/auth-react: <AuthProvider> has not mounted yet. ' +
            'getAccessToken() / getUserManager() may only be called after the ' +
            'first render of the provider.');
    }
    return _um;
}
/**
 * Returns the current access token, or an empty string if unauthenticated.
 * Suitable for `CreditsClient`'s `auth.getToken` and similar non-React seams.
 */
export async function getAccessToken() {
    const u = await getUserManager().getUser();
    if (!u || u.expired)
        return '';
    return u.access_token;
}
//# sourceMappingURL=userManager.js.map